import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Container, Text, Title, Paper, Button, Select, Skeleton, Tooltip, Group, ActionIcon, Menu, Grid } from '@mantine/core';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { DateTime } from 'luxon';
import { useInterval } from './hooks/useInterval';
import { IconLanguage, IconQuestionMark, IconWorld, IconPaperclip, IconClock, IconFile, IconBell } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ACHIEVEMENTS } from './achievements';
import { SUPPORT_PHONE } from './paymentPackages';
import NotificationSettings, { pickReminderSettings } from './NotificationSettings';
import LessonComment from './LessonComment';
import './i18n';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  ChartTooltip,
  Legend
);

// При балансе ≤ этого числа кнопка пополнения становится акцентной с предупреждением
const LOW_BALANCE_THRESHOLD = 3;

function App() {
  const { t, i18n } = useTranslation();
  const { userId } = useParams();
  const [teacherName, setTeacherName] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showAllLessons, setShowAllLessons] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [chartType, setChartType] = useState('weekly');
  const [lessons, setLessons] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [hasHeldLessons, setHasHeldLessons] = useState(null); // null — история ещё не загружена
  const [achievements, setAchievements] = useState([]);
  const [achievementCounts, setAchievementCounts] = useState({}); // { Perfectionist: 3 } — сколько раз получено
  const [lessonBalance, setLessonBalance] = useState(null); // { available, planned } из CRM
  const [error, setError] = useState(null);

  const [timezone, setTimezone] = useState(() => {
    return localStorage.getItem('timezone') || 'America/New_York';
  });
  const [loadingLessonId, setLoadingLessonId] = useState(null);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || (navigator.language.startsWith('ru') ? 'Русский' : 'English');
  });
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [notificationsOpened, setNotificationsOpened] = useState(false);
  const [reminderSettings, setReminderSettings] = useState(null); // поля cReminder* из карточки ученика
  const [reminderContacts, setReminderContacts] = useState({}); // { hasPhone, hasEmail }
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/user/${userId}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.cLanguage) {
          const selectedLanguage = data.cLanguage === 'Russian' ? 'Русский' : 'English';
          setLanguage(selectedLanguage);
          localStorage.setItem('language', selectedLanguage);
        }
        // Невалидная зона сломала бы все даты в кабинете — такую игнорируем
        if (data.cTimezone && DateTime.local().setZone(data.cTimezone).isValid) {
          setTimezone(data.cTimezone);
          localStorage.setItem('timezone', data.cTimezone);
        }
        if (typeof data.cLessonsAvailable === 'number') {
          setLessonBalance({
            available: data.cLessonsAvailable,
            planned: typeof data.cLessonsPlanned === 'number' ? data.cLessonsPlanned : null,
          });
        }
        setReminderSettings(pickReminderSettings(data));
        setReminderContacts({ hasPhone: data.hasPhone, hasEmail: data.hasEmail });
        document.cookie = `lp_client_id=${userId}; domain=.lingva-pro.org; path=/; max-age=2592000; secure; samesite=lax`;
      })
      .catch((error) => {
        console.error('Ошибка при получении данных пользователя:', error);
        setError(t('userNotFoundError'));
      });
  }, [userId]);

  const handleTimezoneChange = async (value) => {
    if (!value) return;
    setTimezone(value);
    localStorage.setItem('timezone', value);

    try {
      const response = await fetch(`${API_BASE_URL}/timezone/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cTimezone: value }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
      console.log('Часовой пояс успешно обновлен');
    } catch (error) {
      console.error('Ошибка при обновлении часового пояса:', error.message);
    }
  };

  const handleLanguageChange = async (value) => {
    if (!value) return;
    setLanguage(value);
    localStorage.setItem('language', value);

    try {
      const response = await fetch(`${API_BASE_URL}/language/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cLanguage: value === 'Русский' ? 'Russian' : 'English',
        }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
      console.log('Язык успешно обновлен');
      window.location.reload();
    } catch (error) {
      console.error('Ошибка при обновлении языка:', error.message);
    }
  };

  const timezones = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'UTC', 'Europe/London', 'Europe/Berlin',
    'Europe/Moscow', 'Asia/Dubai', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
    'America/Sao_Paulo'
  ];

  // Пояс из CRM может быть не из списка (выставил менеджер) — показываем и его
  if (!timezones.includes(timezone)) timezones.push(timezone);
  const timezoneData = timezones.map(tz => ({ value: tz, label: tz.replace('_', ' ') }));
  const languageData = [
    { value: 'Русский', label: 'Русский' },
    { value: 'English', label: 'English' },
  ];

  const handleShowMore = () => {
    setShowAll(true);
  };

  const handleShowMoreLessons = () => {
    setShowAllLessons(true);
  };

  const API_BASE_URL = window.location.hostname === 'alt-service.lingva-pro.org'
    ? window.location.origin + '/api'
    : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');

  const BBB_HOST = window.location.hostname === 'alt-service.lingva-pro.org' ? 'alt.lingva-pro.org' : 'class.lingva-pro.org';

  const isFetchingLessons = useRef(false);

  const fetchLessons = async () => {
    if (isFetchingLessons.current) {
      return;
    }
    isFetchingLessons.current = true;

    try {
      const response = await fetch(`${API_BASE_URL}/lesson/${userId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        console.error(`Ошибка HTTP: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('Некорректный формат данных:', data);
        return;
      }

      if (data.length > 0) {
        setLessons(data);
        setIsCallActive(true);
      } else {
        setLessons([]);
        setIsCallActive(false);
      }
    } catch (error) {
      console.error('Ошибка при получении данных об уроках:', error);
    } finally {
      isFetchingLessons.current = false;
    }
  };

  useEffect(() => {
    fetchLessons();
  }, [userId]);

  useInterval(() => {
    fetchLessons();
  }, 300000);

  useEffect(() => {
    fetch(`${API_BASE_URL}/homework/${userId}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setHomeworks(data);
      })
      .catch((error) => console.error('Ошибка при получении данных о домашних работах:', error));
  }, [userId]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/call/${userId}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setCalls(data);
        setHasHeldLessons(data.some((call) => call.status === 'Held'));
      })
      .catch((error) => {
        console.error('Ошибка при получении данных о звонках:', error);
        setHasHeldLessons(true); // историю не узнали — показываем тексты для действующего ученика
      });
  }, [userId]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/user/${userId}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.Achievement) {
          setAchievements(data.Achievement.split(','));
        }
        setAchievementCounts(data.AchievementCounts || {});
      })
      .catch((error) => {
        console.error('Ошибка при получении данных пользователя:', error);
        setError(t('userNotFoundError'));
      });
  }, [userId]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/call/${userId}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const filteredData = data.filter((call) => call.status !== 'Not Held');

        const daily = filteredData.reduce((acc, call) => {
          const date = call.dateStart.split(' ')[0];
          const hours = call.duration / 3600;
          acc[date] = (acc[date] || 0) + hours;
          return acc;
        }, {});

        const dailyData = Object.entries(daily)
          .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
          .reduce((acc, [date, total]) => {
            const lastTotal = acc.length > 0 ? acc[acc.length - 1].total : 0;
            const cumulativeTotal = lastTotal + total;
            const status = filteredData.find((call) => call.dateStart.startsWith(date))?.status || 'Unknown';
            acc.push({ date, total: cumulativeTotal, status });
            return acc;
          }, []);

        const weekly = filteredData.reduce((acc, call) => {
          const weekStart = DateTime.fromISO(call.dateStart.split(' ')[0])
            .startOf('week')
            .toISODate();
          const hours = call.duration / 3600;

          if (!acc[weekStart]) {
            acc[weekStart] = { Planned: 0, Held: 0 };
          }
          acc[weekStart][call.status] += hours;
          return acc;
        }, {});

        const weeklyData = Object.entries(weekly)
          .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
          .reduce((acc, [week, values]) => {
            const last = acc.length > 0 ? acc[acc.length - 1] : { Planned: 0, Held: 0 };
            const cumulativePlanned = last.Planned + values.Planned;
            const cumulativeHeld = last.Held + values.Held;
            acc.push({ week, Planned: cumulativePlanned, Held: cumulativeHeld });
            return acc;
          }, []);

        setChartData({ dailyData, weeklyData });
      })
      .catch((error) => console.error('Ошибка при получении данных для графиков:', error));
  }, [userId]);

  const [chartData, setChartData] = useState({ dailyData: [], weeklyData: [] });

  const chartDataConfig = chartType === 'daily'
    ? {
        labels: chartData.dailyData.map((item) =>
          DateTime.fromISO(item.date).setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM')
        ),
        datasets: [
          {
            data: chartData.dailyData.map((item) => item.total),
            borderColor: '#0EA5E9',
            backgroundColor: 'rgba(14, 165, 233, 0.15)',
            pointBackgroundColor: chartData.dailyData.map((item) =>
              item.status === 'Planned' ? '#F59E0B' : '#0EA5E9'
            ),
            pointBorderColor: chartData.dailyData.map((item) =>
              item.status === 'Planned' ? '#F59E0B' : '#0EA5E9'
            ),
            pointRadius: 5,
            borderWidth: 3,
            tension: 0.4,
            fill: true,
          },
        ],
      }
    : {
        labels: chartData.weeklyData.map((item) => {
          const weekStart = DateTime.fromISO(item.week).setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM');
          const weekEnd = DateTime.fromISO(item.week).plus({ days: 6 }).setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM');
          return `${weekStart} – ${weekEnd}`;
        }),
        datasets: [
          {
            label: t('heldLessons'),
            data: chartData.weeklyData.map((item) => item.Held),
            backgroundColor: 'rgba(16, 185, 129, 0.8)',
            borderRadius: 6,
          },
          {
            label: t('plannedLessons'),
            data: chartData.weeklyData.map((item) => item.Planned),
            backgroundColor: 'rgba(245, 158, 11, 0.8)',
            borderRadius: 6,
          },
        ],
      };

  if (error) {
    return (
      <Container>
        <Title order={3} c="var(--error)" ta="center" mt="lg">
          {error}
        </Title>
      </Container>
    );
  }

  const formatDate = (utcDate, timezone, isDeadline = false) => {
    if (!utcDate) {
      return null;
    }
    const isoDate = utcDate.replace(' ', 'T');
    const date = DateTime.fromISO(isoDate, { zone: 'utc' });

    // 04:00:00 UTC — так CRM хранит дедлайн домашки без времени. Только для дедлайнов:
    // у урока это реальное время (00:00 по Нью-Йорку летом), его нельзя терять.
    if (isDeadline && date.toFormat('HH:mm:ss') === '04:00:00') {
      return date.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy');
    }

    const localizedDate = date.setZone(timezone);
    if (!localizedDate.isValid) {
      return t('invalidDate');
    }

    const time = localizedDate.toFormat('HH:mm');
    if (isDeadline && time === '00:00') {
      return localizedDate.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy');
    }
    return localizedDate.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy, HH:mm');
  };

  const handleJoinLesson = async (lesson) => {
    setLoadingLessonId(lesson.id);
    try {
      const response = await fetch(`${API_BASE_URL}/createroom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ create: lesson.CreateLink }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      const result = await response.text();
      if (result === 'OK') {
        window.location.href = lesson.studentLink;
      } else {
        console.error('Ошибка создания комнаты:', result);
        alert(t('roomCreationError'));
      }
    } catch (error) {
      console.error('Ошибка при создании комнаты:', error.message);
      alert(t('roomCreationError'));
    } finally {
      setLoadingLessonId(null);
    }
  };

  const calculateTimeLeft = (dateStart) => {
    const isoDateStart = dateStart.replace(' ', 'T');
    const now = DateTime.now().setZone('utc');
    const lessonTime = DateTime.fromISO(isoDateStart, { zone: 'utc' });

    if (!lessonTime.isValid) {
      console.error(`Некорректный формат dateStart: ${dateStart}`);
      return { text: t('invalidDate'), time: { hours: 0, minutes: 0, seconds: 0 } };
    }

    const diff = lessonTime.diff(now, ['hours', 'minutes', 'seconds']).toObject();

    if (diff.hours < 0 || diff.minutes < 0 || diff.seconds < 0) {
      return {
        text: t('lessonStarted'),
        time: lessonTime.diff(now, ['hours', 'minutes', 'seconds']).negate().toObject(),
      };
    }

    return {
      text: t('lessonCountdown'),
      time: diff,
    };
  };

  const LessonCountdown = ({ dateStart }) => {
    const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(dateStart));

    useInterval(() => {
      setTimeLeft(calculateTimeLeft(dateStart));
    }, 1000);

    const { text, time } = timeLeft;

    return (
      <div className="lesson-countdown">
        <Text component="span" c="var(--text-muted)" size="sm">
          {text}
        </Text>
        <Text component="span" c="var(--text-muted)" size="sm" ml={5}>
          {`${Math.abs(Math.floor(time.hours || 0))} ${t('hourShort')} ${Math.abs(Math.floor(time.minutes || 0))} ${t('minuteShort')} ${Math.abs(Math.floor(time.seconds || 0))} ${t('secondShort')}`}
        </Text>
      </div>
    );
  };

  const formatStatus = (status) => {
    const statusMap = {
      Planned: t('statusPlanned'),
      Held: t('statusHeld'),
      'Not Held': t('statusNotHeld'),
    };
    return statusMap[status] || t('unknownStatus');
  };

  const getStatusColor = (status, isCallActive) => {
    if (isCallActive) {
      return 'white';
    }
    const colorMap = {
      Planned: '#0EA5E9',
      Held: '#10B981',
      'Not Held': '#EF4444',
    };
    return colorMap[status] || '#94a3b8';
  };

  const getStatusTextColor = (isCallActive) => {
    return isCallActive ? '#0f172a' : 'white';
  };

  const filteredHomeworks = homeworks.length === 2 &&
    homeworks.some(hw => hw.id === '6839f7de0f1918569') &&
    homeworks.some(hw => hw.id === '6811f5ce4508c59be')
    ? homeworks.filter(hw => hw.id === (language === 'Русский' ? '6811f5ce4508c59be' : '6839f7de0f1918569'))
    : homeworks;

  // Баланс ≤ порога: предупреждение в карточке баланса + оранжевый баннер наверху
  const isLowBalance = lessonBalance !== null && hasHeldLessons !== null
    && lessonBalance.available <= LOW_BALANCE_THRESHOLD;
  const lowBalanceTitle = isLowBalance && (lessonBalance.available > 0
    ? t(hasHeldLessons ? 'lowBalanceTitle' : 'lowBalanceTitleNew', { count: lessonBalance.available })
    : t(hasHeldLessons ? 'noLessonsLeftTitle' : 'noLessonsYetTitle'));
  const lowBalanceText = isLowBalance && (hasHeldLessons
    ? t('lowBalanceText')
    : t(lessonBalance.available > 0 ? 'lowBalanceTextNew' : 'startLearningText'));

  return (
    <div style={{ backgroundColor: '#f0f9ff', minHeight: '100vh', overflowX: 'hidden', position: 'relative' }}>
      {/* Decorative background shapes with parallax */}
      <div style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        opacity: isCallActive ? 0 : 1,
        transition: 'opacity 0.8s ease',
        filter: 'blur(6px)',
      }}>
        {/* Large soft square top-right */}
        <div style={{
          position: 'absolute',
          top: -20 + scrollY * 0.15,
          right: 40,
          width: 200,
          height: 200,
          borderRadius: 32,
          background: 'rgba(14,165,233,0.07)',
        }} />
        {/* Pink circle-outline left */}
        <div style={{
          position: 'absolute',
          top: 350 + scrollY * -0.1,
          left: 30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          border: '4px solid rgba(236,72,153,0.12)',
        }} />
        {/* Rounded square mid-right */}
        <div style={{
          position: 'absolute',
          top: 550 + scrollY * -0.2,
          right: 80,
          width: 90,
          height: 90,
          borderRadius: 16,
          border: '3px solid rgba(245,158,11,0.13)',
          transform: 'rotate(45deg)',
        }} />
        {/* Large circle outline left */}
        <div style={{
          position: 'absolute',
          top: 750 + scrollY * -0.12,
          left: -40,
          width: 280,
          height: 280,
          borderRadius: '50%',
          border: '4px solid rgba(139,92,246,0.08)',
        }} />
        {/* Filled circle right */}
        <div style={{
          position: 'absolute',
          top: 950 + scrollY * -0.25,
          right: 300,
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: 'rgba(139,92,246,0.12)',
        }} />
        {/* Gradient blob bottom-left */}
        <div style={{
          position: 'absolute',
          top: 1200 + scrollY * -0.18,
          left: 80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(20,184,166,0.1) 0%, transparent 70%)',
        }} />
        {/* Filled circle top-left */}
        <div style={{
          position: 'absolute',
          top: 150 + scrollY * -0.05,
          left: 120,
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'rgba(14,165,233,0.15)',
        }} />
        {/* Large soft square center-left */}
        <div style={{
          position: 'absolute',
          top: 530 + scrollY * -0.08,
          left: -30,
          width: 160,
          height: 160,
          borderRadius: 24,
          background: 'rgba(245,158,11,0.06)',
        }} />
        {/* Circle outline top-center */}
        <div style={{
          position: 'absolute',
          top: 80 + scrollY * 0.1,
          left: '40%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          border: '3px solid rgba(20,184,166,0.1)',
        }} />
        {/* Gradient blob mid-right */}
        <div style={{
          position: 'absolute',
          top: 850 + scrollY * -0.15,
          right: -20,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(236,72,153,0.08) 0%, transparent 70%)',
        }} />
        {/* Small rotated square bottom-center */}
        <div style={{
          position: 'absolute',
          top: 1100 + scrollY * -0.22,
          left: '55%',
          width: 70,
          height: 70,
          borderRadius: 12,
          border: '3px solid rgba(14,165,233,0.1)',
          transform: 'rotate(30deg)',
        }} />
        {/* Large circle outline bottom-right */}
        <div style={{
          position: 'absolute',
          top: 1400 + scrollY * -0.1,
          right: 60,
          width: 200,
          height: 200,
          borderRadius: '50%',
          border: '3px solid rgba(245,158,11,0.08)',
        }} />
      </div>

      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Container size={1440} px="xl">
          <Group h={64} justify="space-between">
            <img src="/logo.png" alt="LingvaPro Logo" style={{ height: 40 }} />
            <Group gap="sm" visibleFrom="md">
              <IconWorld size={20} stroke={2} color="#0EA5E9" />
              <Select
                data={timezoneData}
                value={timezone}
                onChange={handleTimezoneChange}
                size="sm"
                styles={{
                  input: { height: 36, minHeight: 36 },
                }}
              />
              <IconLanguage size={20} color="#0EA5E9" />
              <Select
                data={languageData}
                value={language}
                onChange={handleLanguageChange}
                size="sm"
                styles={{
                  input: { height: 36, minHeight: 36 },
                }}
              />
              <Button
                variant="light"
                h={36}
                leftSection={<IconBell size={18} />}
                disabled={!reminderSettings}
                onClick={() => setNotificationsOpened(true)}
              >
                {t('notifications.button')}
              </Button>
            </Group>
            <Group gap={4} hiddenFrom="md">
              <ActionIcon
                variant="subtle"
                size="lg"
                color="dark"
                aria-label={t('notifications.button')}
                disabled={!reminderSettings}
                onClick={() => setNotificationsOpened(true)}
              >
                <IconBell size={20} color="#0EA5E9" />
              </ActionIcon>
              <Menu
                shadow="md"
                width={260}
                opened={mobileMenuOpened}
                onChange={setMobileMenuOpened}
              >
                <Menu.Target>
                  <ActionIcon variant="subtle" size="lg" color="dark">
                    <IconLanguage size={20} color="#0EA5E9" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown p="sm">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Select
                      data={timezoneData}
                      value={timezone}
                      onChange={handleTimezoneChange}
                      size="sm"
                    />
                    <Select
                      data={languageData}
                      value={language}
                      onChange={handleLanguageChange}
                      size="sm"
                    />
                  </div>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Container>
      </header>

      <NotificationSettings
        apiBaseUrl={API_BASE_URL}
        userId={userId}
        timezone={timezone}
        settings={reminderSettings}
        contacts={reminderContacts}
        opened={notificationsOpened}
        onClose={() => setNotificationsOpened(false)}
        onSaved={setReminderSettings}
      />

      <Container size={1440} px="xl">
        {/* Active Lesson Banner */}
        {isCallActive && lessons.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 20 }}>
            {lessons.map((lesson, idx) => (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
              >
                <Paper
                  className="glass-card no-hover"
                  p="lg"
                  style={{ cursor: 'default' }}
                >
                  <div className="lesson-card-inner">
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <Title order={4} fw={700}>
                        {t('lessonScheduled', {
                          lessonDate: formatDate(lesson.dateStart, timezone),
                          teacherName: lesson.teacherName,
                        })}
                      </Title>
                      <div className="lesson-detail-row">
                        <Group gap={5} mb={{ base: 5, md: 0 }}>
                          <IconClock size={20} stroke={2} color="#0EA5E9" />
                          <Text size="sm" c="var(--text-muted)">
                            {t('lessonDuration', { duration: Math.floor(lesson.duration / 60) })}
                          </Text>
                        </Group>
                        <LessonCountdown dateStart={lesson.dateStart} />
                      </div>
                    </div>
                    <div className="lesson-join-btn">
                      <Button
                        className="gradient-btn"
                        size="lg"
                        onClick={() => handleJoinLesson(lesson)}
                        loading={loadingLessonId === lesson.id}
                      >
                        {t('joinLesson')}
                      </Button>
                    </div>
                  </div>
                </Paper>
              </motion.div>
            ))}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: 100,
              border: '2px dashed rgba(14, 165, 233, 0.3)',
              borderRadius: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            <Text c="var(--text-muted)">
              {t('lessonEntryPlaceholder')}
            </Text>
          </div>
        )}

        {/* Low Balance Banner — в форме карточки урока, оранжевый */}
        {isLowBalance && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginTop: 24 }}
          >
            <Paper
              className="glass-card no-hover"
              p="lg"
              style={{ cursor: 'default', background: '#FFF7ED', border: '1px solid #FDBA74' }}
            >
              <div className="lesson-card-inner">
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <Title order={4} fw={700}>
                    {lowBalanceTitle}
                  </Title>
                  <Text size="sm" mt={10}>
                    {lowBalanceText}
                  </Text>
                  <Text size="sm" c="var(--text-muted)" mt={4}>
                    {t('lowBalanceContactText')}{' '}
                    <a href={SUPPORT_PHONE.href} style={{ color: '#EA580C', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      {SUPPORT_PHONE.label}
                    </a>
                  </Text>
                </div>
                <div className="lesson-join-btn">
                  <Button component={RouterLink} to={`/${userId}/payment`} size="lg" color="orange">
                    {t('topUpBalance')}
                  </Button>
                </div>
              </div>
            </Paper>
          </motion.div>
        )}

        {/* Homework Section */}
        <Group mt={80} gap={8} align="center">
          <Title order={3} ta="left" fw={700}>
            {t('homeworksTitle')}
          </Title>
          <Tooltip
            multiline
            w={300}
            withArrow
            label={
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('homeworkLegendTitle')}</div>
                <div style={{ marginBottom: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#F59E0B', marginRight: 6 }} />{t('homeworkLegendNew')}</div>
                <div style={{ marginBottom: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#38BDF8', marginRight: 6 }} />{t('homeworkLegendStarted')}</div>
                <div style={{ marginBottom: 4 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#8B5CF6', marginRight: 6 }} />{t('homeworkLegendOnReview')}</div>
                <div><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10B981', marginRight: 6 }} />{t('homeworkLegendCompleted')}</div>
              </div>
            }
          >
            <ActionIcon variant="filled" color="gray" size="sm" radius="xl">
              <IconQuestionMark size={14} stroke={3} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Grid gap="md" mt="lg">
          {homeworks.length === 0 ? (
            Array.from({ length: 2 }).map((_, index) => (
              <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                <Skeleton height={208} radius="xl" />
              </Grid.Col>
            ))
          ) : (
            filteredHomeworks.slice(0, showAll ? filteredHomeworks.length : 5).map((homework, index) => {
              const imageIndex = index % 10;
              const imagePath = `/Homework_image/${imageIndex}.svg`;

              const borderColor = {
                'Not Started': '#F59E0B',
                'Started': '#38BDF8',
                'Assignment': '#8B5CF6',
                'Completed': '#10B981',
                'Canceled': '#94a3b8',
                'Deferred': '#94a3b8',
              }[homework.status] || '#94a3b8';

              const backgroundColor = isCallActive
                ? 'rgba(255, 255, 255, 0.7)'
                : {
                    'Not Started': 'rgba(245, 158, 11, 0.06)',
                    'Started': 'rgba(56, 189, 248, 0.06)',
                    'Assignment': 'rgba(139, 92, 246, 0.06)',
                    'Completed': 'rgba(16, 185, 129, 0.06)',
                    'Canceled': 'rgba(148, 163, 184, 0.06)',
                    'Deferred': 'rgba(148, 163, 184, 0.06)',
                  }[homework.status] || 'rgba(148, 163, 184, 0.06)';

              const statusText = {
                'Completed': t('homeworkCompletedText'),
                'Canceled': t('homeworkCanceledText'),
                'Deferred': t('homeworkDeferredText'),
              }[homework.status];

              return (
                <Grid.Col
                  key={homework.id}
                  span={{ base: index === 0 ? 12 : 6, md: index === 0 ? 8 : 4 }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                  >
                    <Paper
                      p="lg"
                      radius="lg"
                      className={`homework-card${homework.status === 'Not Started' ? ' homework-shimmer' : ''}`}
                      style={{
                        border: `2px solid ${borderColor}`,
                        backgroundColor: backgroundColor,
                        backdropFilter: 'blur(12px)',
                      }}
                      onClick={() => {
                        window.location.href = `/${userId}/homework/${homework.id}`;
                      }}
                    >
                      <Text
                        fw={700}
                        mb="sm"
                        className="homework-title"
                      >
                        {homework.name.length > 48
                          ? `${homework.name.slice(0, 45)}...`
                          : homework.name}
                      </Text>
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', flex: 1 }}>
                        <div style={{ textAlign: 'left' }}>
                          {index === 0 && (
                            <Text size="sm" fs="italic" mb={5}>
                              {(() => {
                                const previewText = homework.description || '';
                                if (previewText.length > 110) {
                                  const truncated = previewText.slice(0, 110).split(' ').slice(0, -1).join(' ');
                                  return `${truncated}...`;
                                }
                                return previewText;
                              })()}
                            </Text>
                          )}
                          {homework.status === 'Completed' || homework.status === 'Canceled' || homework.status === 'Deferred' ? (
                            <Text size="sm" fw={700} mb={5}>
                              {statusText}
                            </Text>
                          ) : (
                            <>
                              {formatDate(homework.dateEnd, timezone, true) && (
                                <Text size="sm" mb={5}>
                                  {t('deadline')}: {formatDate(homework.dateEnd, timezone, true)}
                                </Text>
                              )}
                              {homework.status === 'Assignment' && (
                                <Text size="sm" fs="italic" mb={5}>
                                  {t('homeworkOnReview')}
                                </Text>
                              )}
                            </>
                          )}
                          <Group gap="sm" mt="sm">
                            {homework.boolLink && (
                              <IconPaperclip size={20} stroke={2} color="#0EA5E9" />
                            )}
                            {homework.boolAttachment && (
                              <IconFile size={20} stroke={2} color="#0EA5E9" />
                            )}
                          </Group>
                        </div>
                        <div style={{ alignSelf: 'center' }}>
                          <img
                            src={imagePath}
                            alt={`Homework ${index + 1}`}
                            style={{ width: 'min(100px, 10vw)', height: 'min(100px, 10vw)', minWidth: 50, minHeight: 50 }}
                          />
                        </div>
                      </div>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              );
            })
          )}
        </Grid>
        {!showAll && homeworks.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Button
              variant="outline"
              color="gray"
              onClick={handleShowMore}
            >
              {t('showMoreTasks')}
            </Button>
          </div>
        )}

        {/* Schedule & Progress Section */}
        <Title order={3} mt={80} ta="left" fw={700}>
          {t('scheduleAndProgressTitle')}
        </Title>
        <Grid gap="md" mt="lg">
          {/* Balance & Call History. Обе колонки растягиваются до высоты ряда:
              нижние карточки (история и ачивки) добирают остаток, низ совпадает */}
          <Grid.Col span={{ base: 12, md: 4 }} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {lessonBalance && hasHeldLessons !== null && (
              <Paper
                p="lg"
                radius="xl"
                className="glass-card no-hover"
                style={{ flexShrink: 0, cursor: 'default' }}
              >
                {/* Заголовок предупреждения не дублируем — он уже в баннере наверху */}
                <Text fw={700} size="lg" mb={isLowBalance ? 4 : 'sm'} style={{ width: '100%' }}>
                  {t('availableLessons')}: {lessonBalance.available}
                  {lessonBalance.planned !== null && (
                    <span style={{ opacity: 0.6 }}> ({t('plannedLessonsCount', { count: lessonBalance.planned })})</span>
                  )}
                </Text>
                {isLowBalance && (
                  <Text size="sm" c="dimmed" mb="sm">
                    {lowBalanceText}
                  </Text>
                )}
                <Button
                  component={RouterLink}
                  to={`/${userId}/payment`}
                  fullWidth
                  {...(isLowBalance ? { size: 'lg', color: 'orange' } : { variant: 'light' })}
                >
                  {t('topUpBalance')}
                </Button>
              </Paper>
            )}
            <Paper
              p="lg"
              radius="xl"
              className="glass-card no-hover"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                cursor: 'default',
              }}
            >
              {calls.length === 0 ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} style={{ width: '100%', marginBottom: 10 }}>
                    <Skeleton height={30} width="80%" mb={5} />
                    <Skeleton height={20} width="60%" />
                    {index < 4 && (
                      <div style={{
                        width: '100%',
                        height: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                        marginTop: 30,
                      }} />
                    )}
                  </div>
                ))
              ) : (
                calls.slice(0, showAllLessons ? calls.length : 5).map((call, index) => {
                  const displayedCalls = calls.slice(0, showAllLessons ? calls.length : 5);
                  const lessonAchievement = call.achievement && ACHIEVEMENTS.find((a) => a.en === call.achievement);
                  return (
                    <div key={call.id} style={{ width: '100%', marginBottom: index < displayedCalls.length - 1 ? 10 : 0 }}>
                      <Group gap="sm" mb={5}>
                        <Text fw={700} size="sm">
                          {formatDate(call.dateStart, timezone)}
                        </Text>
                        <span
                          style={{
                            backgroundColor: getStatusColor(call.status, isCallActive),
                            color: getStatusTextColor(isCallActive),
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            border: isCallActive ? '1px solid #e2e8f0' : 'none',
                          }}
                        >
                          {formatStatus(call.status)}
                        </span>
                        {lessonAchievement && (
                          // Достижение, выданное учителем за этот урок
                          <Tooltip
                            label={
                              <div>
                                <Text size="xs" fw={700}>{t(lessonAchievement.title)}</Text>
                                <Text size="xs">{t(lessonAchievement.hint)}</Text>
                              </div>
                            }
                            withArrow
                            multiline
                            w={220}
                          >
                            <img
                              src={lessonAchievement.image}
                              alt={t(lessonAchievement.title)}
                              width={28}
                              height={28}
                              style={{ display: 'block', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )}
                      </Group>
                      <Text size="sm" mb={call.cComment ? 'sm' : 0}>
                        {`${t('lessonWith')} ${call.teacherName}`}
                      </Text>
                      {call.cComment && <LessonComment comment={call.cComment} />}
                      {index < displayedCalls.length - 1 && (
                        <div style={{
                          width: '100%',
                          height: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.06)',
                          marginTop: 30,
                        }} />
                      )}
                    </div>
                  );
                })
              )}
              {!showAllLessons && calls.length > 5 && (
                <div style={{ textAlign: 'center', marginTop: 20, width: '100%' }}>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={handleShowMoreLessons}
                  >
                    {t('showMoreLessons')}
                  </Button>
                </div>
              )}
            </Paper>
          </Grid.Col>

          {/* Chart & Achievements */}
          <Grid.Col span={{ base: 12, md: 8 }} style={{ display: 'flex', flexDirection: 'column' }}>
            <Paper
              p="lg"
              radius="xl"
              className="glass-card no-hover"
              style={{
                height: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                cursor: 'default',
              }}
            >
              <Button.Group mb="lg">
                <Button
                  variant={chartType === 'daily' ? 'light' : 'default'}
                  color="gray"
                  onClick={() => setChartType('daily')}
                >
                  {t('dailyButton')}
                </Button>
                <Button
                  variant={chartType === 'weekly' ? 'light' : 'default'}
                  color="gray"
                  onClick={() => setChartType('weekly')}
                >
                  {t('weeklyButton')}
                </Button>
              </Button.Group>
              <div style={{ width: 'calc(100% - 40px)', height: 'calc(100% - 60px)' }}>
                {chartType === 'daily' ? (
                  <Line
                    data={chartDataConfig}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: (context) => {
                              const value = context.raw;
                              const hours = Math.floor(value);
                              const minutes = Math.round((value - hours) * 60);
                              const pluralize = (n, one, few, many) => {
                                const mod10 = n % 10;
                                const mod100 = n % 100;
                                if (mod10 === 1 && mod100 !== 11) return one;
                                if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
                                return many;
                              };
                              const hourLabel = pluralize(hours, t('hourSingular'), t('hourPlural'), t('hourMany'));
                              const minuteLabel = pluralize(minutes, t('minuteSingular'), t('minutePlural'), t('minuteMany'));
                              return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: { title: { display: false } },
                        y: { title: { display: false }, beginAtZero: true },
                      },
                    }}
                  />
                ) : (
                  <Bar
                    data={chartDataConfig}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                          labels: { font: { size: 12 } },
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false,
                          callbacks: {
                            label: (context) => {
                              const value = context.raw;
                              const hours = Math.floor(value);
                              const minutes = Math.round((value - hours) * 60);
                              const pluralize = (n, one, few, many) => {
                                const mod10 = n % 10;
                                const mod100 = n % 100;
                                if (mod10 === 1 && mod100 !== 11) return one;
                                if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
                                return many;
                              };
                              const hourLabel = pluralize(hours, t('hourSingular'), t('hourPlural'), t('hourMany'));
                              const minuteLabel = pluralize(minutes, t('minuteSingular'), t('minutePlural'), t('minuteMany'));
                              return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
                            },
                            footer: (tooltipItems) => {
                              const planned = tooltipItems.find((item) => item.dataset.label === t('plannedLessons'));
                              const held = tooltipItems.find((item) => item.dataset.label === t('heldLessons'));
                              const plannedValue = planned ? planned.raw : 0;
                              const heldValue = held ? held.raw : 0;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          stacked: true,
                          title: { display: false },
                          ticks: { display: false },
                        },
                        y: {
                          stacked: true,
                          title: { display: false },
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                )}
              </div>
            </Paper>

            {/* Achievements */}
            <Paper
              p="lg"
              radius="xl"
              className="glass-card no-hover"
              style={{
                minHeight: 280,
                // растягиваем до низа истории уроков, но не когда список развёрнут полностью
                flex: showAllLessons ? 'none' : 1,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
                justifyContent: 'center',
                gap: 10,
                cursor: 'default',
              }}
            >
              {ACHIEVEMENTS.map((achievement) => (
                <Tooltip
                  key={achievement.en}
                  label={
                    <div>
                      <Text size="xs" fw={700}>{t(achievement.title)}</Text>
                      <Text size="xs">{t(achievement.hint)}</Text>
                    </div>
                  }
                  position="bottom"
                  withArrow
                  multiline
                  w={220}
                >
                  <div className="achievement-box">
                    <img
                      src={achievement.image}
                      alt={t(achievement.title)}
                      className={`achievement-image ${achievements.includes(achievement.en) ? '' : 'locked'}`}
                    />
                    {achievementCounts[achievement.en] > 1 && (
                      <span className="achievement-count">×{achievementCounts[achievement.en]}</span>
                    )}
                  </div>
                </Tooltip>
              ))}
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>

      {/* Footer */}
      <div style={{ height: 40 }} />
      <Text ta="center" size="xs" c="var(--primary)" mb="sm">
        <RouterLink
          to={`/${userId}/guide`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('platformInstructions')}
        </RouterLink>
        {' | '}
        <a
          href={`https://${BBB_HOST}/quality/${userId}?lg=${i18n.language}`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('videoConnectionTest')}
        </a>
      </Text>

      <style>
        {`
          .lesson-card-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
          @media (min-width: 768px) {
            .lesson-card-inner {
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            }
          }

          .lesson-detail-row {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-top: 10px;
          }
          @media (min-width: 768px) {
            .lesson-detail-row {
              flex-direction: row;
              align-items: center;
            }
          }

          .lesson-join-btn {
            margin-top: 20px;
            align-self: center;
            text-align: center;
          }
          @media (min-width: 768px) {
            .lesson-join-btn {
              margin-top: 0;
              margin-left: 20px;
              text-align: right;
            }
          }

          .lesson-countdown {
            text-align: left;
            margin-left: 20px;
          }
          @media (min-width: 768px) {
            .lesson-countdown {
              text-align: right;
            }
          }

          .homework-card {
            display: flex;
            flex-direction: column;
            height: 230px;
            box-sizing: border-box;
            transition: all 0.3s ease;
            cursor: pointer;
          }
          @media (min-width: 768px) {
            .homework-card {
              height: 208px;
            }
          }
          .homework-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 32px rgba(14, 165, 233, 0.15) !important;
            border-color: rgba(14, 165, 233, 0.3) !important;
          }

          .homework-title {
            font-size: 0.9rem;
          }
          @media (min-width: 576px) {
            .homework-title {
              font-size: 1.1rem;
            }
          }
          @media (min-width: 992px) {
            .homework-title {
              font-size: 1.25rem;
            }
          }

          .lesson-comment {
            position: relative;
            padding: 10px;
            background-color: rgba(14, 165, 233, 0.04);
            border: 1px solid rgba(14, 165, 233, 0.1);
            border-radius: 12px;
          }
          /* та же ширина, что у текста короткого комментария (минус иконка и отступ) */
          .lesson-comment-measure {
            position: absolute;
            top: 0;
            left: 34px;
            right: 10px;
            visibility: hidden;
            white-space: nowrap;
            overflow: hidden;
            pointer-events: none;
          }
          .lesson-comment-header {
            display: block;
            width: 100%;
          }
          .lesson-comment-chevron {
            color: var(--text-muted);
            flex-shrink: 0;
            transition: transform 0.2s ease;
          }
          .lesson-comment-chevron.open {
            transform: rotate(180deg);
          }

          .achievement-box {
            position: relative;
            width: 80px;
            height: 80px;
            cursor: pointer;
          }

          .achievement-image {
            width: 100%;
            height: 100%;
            transition: filter 0.3s ease, opacity 0.3s ease;
          }

          .achievement-image.locked {
            filter: grayscale(100%);
            opacity: 0.3;
          }

          .achievement-count {
            position: absolute;
            right: -2px;
            bottom: -2px;
            min-width: 26px;
            padding: 1px 6px;
            border-radius: 999px;
            background: #8B5CF6;
            color: white;
            font-size: 12px;
            font-weight: 700;
            line-height: 18px;
            text-align: center;
            box-shadow: 0 0 0 2px white;
          }

        `}
      </style>
    </div>
  );
}

export default App;
